<template>
  <queueton-resource-status v-if="event && event.id" :resource-id="event.id" resource="event" :post-processing="getEvent">
    <template #default="queueResource">
      <div class="w-full md:pl-10 md:w-3/4">
        <h1 class="mt-2 mb-4 font-sans-bold">Admin Settings</h1>

        <div class="mb-12">
            <label for="festival" ref="festival">
                Select Event Calendar <span class="text-red">*</span>
            </label>
            <select id="festival" name="festival"
                v-model="form.festival_id"
                v-validate="'required'">
                <option :value="null" disabled>Select an event calendar</option>
                <option
                    v-for="festival in currentFestivals"
                    :key="festival.id"
                    :value="festival.id"
                    :selected="event.festival.id == festival.id">{{ festival.title }} {{festival.datesTitle}}</option>
            </select>
            <span v-if="errors.has('festival')" class="error text-error-red text-lg">
              {{ errors.first('festival').replaceAll('festival', 'event calendar') }}
            </span>
        </div>

        <div class="mb-12" v-if="hasThemes">
          <label for="theme">
            Select Calendar Theme
          </label>

          <div class="group__checkbox">
            <div class="flex flex-wrap select-box">
              <template v-for="theme in themes">
                <label class="w-full text-base font-normal md:w-1/2 md:pr-3" :key="theme.id">
                  <input
                    type="checkbox"
                    v-model="form.themes"
                    :value="theme.id"
                  />
                  <span class="inline-block mr-2 check"></span> {{ theme.title }}
                </label>
              </template>
            </div>
          </div>
        </div>

        <label>
          Publishing
        </label>
        <div class="mb-4 group__checkbox">
          <div class="select-box">
            <label class="text-base font-normal" for="published">
              <template v-if="queueResource.isProcessing">
                <input type="checkbox" :checked="form.published" :disabled="true" class="mr-3">
                <span class="check inline-block mr-2" />
                <template v-if="form.published">
                  Publishing...
                </template>
                <template v-else>
                  Unpublishing...
                </template>
              </template>
              <template v-else>
                <input type="checkbox" id="published" v-model="form.published" class="mr-3">
                <span class="check inline-block mr-2" /> Published
              </template>
            </label>
          </div>
        </div>
        <div class="mb-4 group__checkbox">
          <div class="select-box">
            <label class="text-base font-normal" for="third-party">
              <input type="checkbox" id="third-party" v-model="form.allowThirdParty" class="mr-3">
              <span class="check inline-block mr-2" /> Allow third party ticketing
            </label>
          </div>
        </div>
        <div class="mb-4 group__checkbox">
        </div>
        <div class="my-6 mb-12 text-right">
          <button class="button mr-4" @click="save" :class="{ 'opacity-50 cursor-not-allowed': saving || queueResource.isProcessing }" :disabled="saving || queueResource.isProcessing">
            <template v-if="saving">Processing...</template>
            <template v-else>Save</template>
          </button>
          <button class="button" @click="saveNext" :class="{ 'opacity-50 cursor-not-allowed': saving || queueResource.isProcessing }" :disabled="saving || queueResource.isProcessing">
            Save & Next
          </button>
        </div>
        <div class="pt-8 border-t border-rose-600">
          <p class="mb-6">Deleting an event will unpublish the event and archive so it does not show.</p>
          <button class="button bg-red-dark" @click="deleteEvent">
            Delete
          </button>
        </div>
      </div>
    </template>
  </queueton-resource-status>
</template>

<script>
import { mapGetters } from "vuex";
import eventSave from "../../../mixins/event-save";
import messaging from "../../../mixins/messaging";
import QueuetonResourceStatus from "../../queueton/QueuetonResourceStatus.vue";
import RadioGroup from "../../global/RadioGroup.vue";
import _ from 'lodash';

export default {
  name: "EventCreateAdmin",
  components: { QueuetonResourceStatus, RadioGroup },
  mixins: [eventSave, messaging],
  data: () => ({
    form: {
        festival_id: null,
        themes: [],
        published: false,
        allowThirdParty: false,
        archive: false
    },
    checkedNames: [],
    next: "events-basics"
  }),
  computed: {
    ...mapGetters({
        event: "event",
        festivals: "festivals",
        themes: "themes",
    }),
    currentFestivals() {
        return _.filter(this.festivals, { isInPast: false });
    },
    queueStatus() {
      const eventId = this.event ? this.event.id : -1;
      return this.$store.getters['queueton/resourceStatusOrig']('event', eventId);
    },
    hasThemes() {
      return ! _.isEmpty(this.themes)
    }
  },
  watch: {
    event(event) {
      if (!_.isEmpty(event)) {
        this.form.published = event.published;
        this.form.allowThirdParty = event.allowThirdParty;
        this.form.festival_id = event.festival.id;
        this.form.archive = event.deleted_at ? true : false;

        this.handleThemeUpdate()

        this.$nextTick(function() {
          this.$store.dispatch("setUnsavedData", false);
        });
      }
    }
  },
  methods: {
    async save() {
      this.saving = true;

      const result = await this.$validator.validateAll();

      if (!result) {
        this.saving = false;
        return;
      }

      this.$toasted.show("Event currently saving...");

      let route = this.$laroute.route("api.events.update.admin", {
        event: this.event.id
      });

      try {
        await axios.put(route, this.form);

        this.showUpdatedToast();
        await this.$store.dispatch("setUnsavedData", false);
      } catch (e) {
        this.displayError(e);
      }

      this.saving = false;
    },
    handleThemeUpdate() {
      let themeIds = _.map(this.event.themes, 'id');
      this.$set(this.form, 'themes', themeIds);
    },
    getEvent() {
      const id = this.$route.params.id;
      this.$store.dispatch("getEvent", { id: id });
    },
    deleteEvent() {
      let confirm = window.confirm('Are you sure you want to delete this event?')

      if(confirm) {

        let route = this.$laroute.route("api.events.delete", {
          event: this.event.id
        });

        let response = axios.delete(route);
        if(response) {
          this.$store.dispatch("getMyEvents");
          this.$router.push(`/events`);
        }
      }
    }
  },
  mounted() {
    this.getEvent();
    this.$store.dispatch("getFestivals");
    this.$store.dispatch("getCategories");
    this.$store.dispatch("getThemes");
    document.getElementById("dropdown").checked = false;
  }
};
</script>
